import Color from 'color';

import CreditorService from '-/services/CreditorService';
import theme from '-/styles/theme';
import {merge} from '-/util/lodash';

export const rem = (number, root = 16) => {
    return number / root + 'rem';
};

export const center = (direction, position = 'absolute') => {
    const result = {
        position,
        transformStyle: 'preserve-3d'
    };

    switch(direction) {
        case 'both':
            result.top = '50%';
            result.left = '50%';
            result.transform = 'translate(-50%, -50%)';
            break;
        case 'horizontal':
            result.left = '50%';
            result.transform = 'translate(-50%, 0)';
            break;
        case 'vertical':
            result.top = '50%';
            result.transform = 'translate(0, -50%)';
            break;
    }

    return result;
};

/**
 * Self-hosted, like Avenir beside it, rather than pulled from Google Fonts.
 *
 * Three reasons this is not a `<link>` to fonts.googleapis.com:
 *
 *  - The production HTML is generated server-side by the ServiceLayer
 *    (`generateRefiIndexReact`), so a stylesheet link would be a cross-repo change and a
 *    ServiceLayer deploy every time the font list moves. `public/fonts/` is already
 *    served at `/fonts/` and needs nothing outside this repo.
 *  - Google Fonts costs a member two chained round trips to a third party -- CSS, then
 *    the font -- and reveals their IP to Google on every view of a page that goes on to
 *    collect an SSN. The historic upside, a shared cross-site cache, has been gone since
 *    browsers partitioned the HTTP cache by origin.
 *  - It would depend on `fonts.googleapis.com` and `fonts.gstatic.com` staying in the
 *    CSP (nginx-configs `snippets/security-headers.conf`), which is maintained per
 *    environment branch and drifts. Same-origin fonts need no allowance at all.
 *
 * Montserrat is SIL OFL 1.1, which permits this; the notice ships as
 * `public/fonts/Montserrat-OFL.txt`.
 *
 * `font-display: swap` on the Montserrat faces so the copy paints immediately in the
 * fallback rather than staying invisible while a webfont loads.
 */
export const fontFace = () => {
    return {
        '@font-face': [
            {
                fontFamily: 'Montserrat',
                fontDisplay: 'swap',
                fontStyle: 'normal',
                fontWeight: 400,
                src: 'url("/fonts/Montserrat-400.woff2") format("woff2")'
            },
            {
                fontFamily: 'Montserrat',
                fontDisplay: 'swap',
                fontStyle: 'normal',
                fontWeight: 700,
                src: 'url("/fonts/Montserrat-700.woff2") format("woff2")'
            },
            {
                fontFamily: 'Avenir',
                fontStyle: 'normal',
                fontWeight: 'bold',
                src: 'local("AvenirNextLTPro-Bold"), url("/fonts/AvenirNextLTPro-Bold.woff") format("woff")'
            },
            {
                fontFamily: 'Avenir',
                fontStyle: 'normal',
                fontWeight: 'normal',
                src: 'local("AvenirNextLTPro-Regular"), url("/fonts/AvenirNextLTPro-Regular.woff") format("woff")'
            }
        ]
    };
};

export const position = (opts = {}) => {
    const {position = 'relative', top = null, right = null, bottom = null, left = null} = opts;

    return {
        position,
        ...top && {top: typeof top === 'number' ? px(top) : top},
        ...right && {right: typeof right === 'number' ? px(right) : right},
        ...bottom && {bottom: typeof bottom === 'number' ? px(bottom) : bottom},
        ...left && {left: typeof left === 'number' ? px(left) : left}
    };
};

export const mediumScreen = content => {
    return screenSize(content, 568);
};

export const smallScreen = content => {
    return screenSize(content, 670);
};

export const tinyScreen = content => {
    return screenSize(content, 560);
};

export const phoneScreen = content => {
    return screenSize(content, 736);
};

export const px = num => {
    return `${num}px`;
};

const screenSize = (content, size) => {
    return {
        [`@media all and (max-device-height: ${px(size)})`]: content
    };
};

export const size = (width, height) => {
    return {
        width,
        height: typeof height !== 'undefined' ? height : width
    };
};

export const truncateText = width => {
    return {
        maxWidth: px(width),
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block'
    };
};

export const userSelect = select => {
    return {
        userSelect: select
    };
};

export const wordWrap = () => {
    return {
        overflowWrap: 'break-word',
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        hyphens: 'auto'
    };
};

export const bounce = () => {
    return {
        animation: 'bounce 0.6s normal'
    };
};

export const spin = () => {
    return {
        animation: 'spin 2s infinite linear'
    };
};

export const inputPlaceholder = content => {
    const baseStyle = {
        fontFamily: theme.fontFamily,
        fontSize: rem(14),
        fontWeight: 'normal'
    };

    return {
        // modern, chrome
        '&::placeholder': {
            ...baseStyle,
            ...content
        },
        // edge
        '&::-ms-input-placeholder': {
            ...baseStyle,
            ...content
        },
        // IE 11
        '&:-ms-input-placeholder': {
            ...baseStyle,
            ...content
        }
    };
};


export const fadeout = () => {
    return {
        /*'@keyframes fadeout': {
            '0%': {
                opacity: '1'
            },
            '100%': {
                opacity: '0'
            }
        },*/
        animation: 'fadeout 1.6s normal'
    };
};

export const standardFont = () => {
    return {
        fontFamily: theme.fontFamily,
        fontSize: rem(18),
        lineHeight: rem(25),
        color: theme.colors.strong
    };
};

export const important = value => {
    return [[value], '!important'];
};

export const lighten = (color, percent) => {
    const colorInstance = Color(color);
    const result = colorInstance.lighten(percent).hex();
    return result;
};

export const getTheme = () => {
    const creditorTheme = CreditorService.getTheme();

    return {
        ...merge(theme, creditorTheme),
        fullTheme: !!creditorTheme
    };
};

export const hasFullTheme = () => {
    return getTheme().fullTheme;
};
